import React, { useState } from "react";
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Collapse,
  styled,
  IconButton,
} from "@mui/material";
import TablePagination from "../../components/TablePagination/TablePagination";
import IconButtons from "../../components/IconButtons/IconButtons";
import {
  associate_question_default_icon,
  associate_question_disabled_icon,
  delete_active_icon,
  delete_disabled_icon,
  edit_active_icon,
  edit_disabled_icon,
} from "../../constants/icons";

import { SingleWordCount, font_13_icon, getDate } from "../../utils/utils";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import dayjs from "dayjs";
import UserTableAvatar from "../UserTableAvatar/UserTableAvatar";
import { HelpTexts } from "../../constants/constants";
const { ASSOCIATED_QUESTION_NOT_EDITABLE } = HelpTexts;

// Helper function to calculate question display properties
const getQuestionDisplayProps = (question) => {
  const wordCount = question?.length;
  const countLines = question?.trim()?.split(/\r|\r\n|\n/);
  const lineLength = countLines?.length;
  const singleWord = SingleWordCount(question);
  const shouldShowViewMore = wordCount > 200 || lineLength > 3;
  const collapsedSize =
    wordCount > 100 || lineLength > 2
      ? singleWord?.length < 3 && wordCount > 100
        ? 20
        : 60
      : lineLength * 20;
  const collapseHeight =
    singleWord?.length < 3 && wordCount > 100 ? "max-content" : 0;

  return {
    wordCount,
    lineLength,
    singleWord,
    shouldShowViewMore,
    collapsedSize,
    collapseHeight,
  };
};

// Component for Question Cell with Collapse
const QuestionCell = ({
  question,
  questionId,
  viewMore,
  viewMoreId,
  handleViewMoreClick,
  ExpandMore,
}) => {
  const { shouldShowViewMore, collapsedSize, collapseHeight } =
    getQuestionDisplayProps(question);
  const isExpanded = viewMore && viewMoreId === questionId;

  return (
    <TableCell align="left" className="padding-left-1">
      <Collapse
        in={isExpanded}
        collapsedSize={collapsedSize}
        sx={{ height: collapseHeight }}
      >
        <Typography sx={{ whiteSpace: "pre-wrap" }} variant="font_12">
          {question}
        </Typography>
      </Collapse>
      {shouldShowViewMore && (
        <Box onClick={() => handleViewMoreClick(questionId)}>
          <Typography
            variant="font_9_bold"
            color="secondary"
            cursor="pointer"
            sx={{ fontWeight: 600 }}
          >
            {isExpanded ? "VIEW LESS" : "VIEW MORE"}
          </Typography>
          <ExpandMore expand={isExpanded} sx={{ padding: 0 }}>
            <ExpandMoreIcon color="secondary" />
          </ExpandMore>
        </Box>
      )}
    </TableCell>
  );
};

// Helper function to get edit button disabled state
const getEditButtonDisabled = (
  viaTopic,
  viaSession,
  hasTopicId,
  isDisabled,
  row
) => {
  if (viaTopic && !row?.permission?.canEditQuestion) return true;
  if (viaSession && hasTopicId) return true;
  return isDisabled;
};

// Helper function to get edit button tooltip
const getEditButtonTooltip = (
  viaTopic,
  viaSession,
  hasTopicId,
  isDisabled,
  row
) => {
  if (viaTopic && !row?.permission?.canEditQuestion) {
    return `This Question can be edited by SPOCs of ${row?.topic?.competency?.name} competency`;
  }
  if (viaSession && hasTopicId) return ASSOCIATED_QUESTION_NOT_EDITABLE;
  if (isDisabled) return "Module is completed";
  return null;
};

// Helper function to get delete button disabled state
const getDeleteButtonDisabled = (
  viaTopic,
  viaSession,
  hasTopicId,
  isDisabled,
  row
) => {
  if (viaTopic && !row?.permission?.canDeleteQuestion) return true;
  if (viaSession && isDisabled) return true;
  return false;
};

// Helper function to get delete button tooltip
const getDeleteButtonTooltip = (viaTopic, viaSession, isDisabled, row) => {
  if (viaTopic && !row?.permission?.canDeleteQuestion) {
    return `This Question can be deleted by SPOCs of ${row?.topic?.competency?.name} competency`;
  }
  if (isDisabled) return "Module is completed";
  return null;
};

// Component for Action Buttons
const ActionButtons = ({
  row,
  hasTopicId,
  viaTopic,
  viaSession,
  isDisabled,
  questionEditModal,
  handleOpenDisassociate,
  handleDeleteModalOpen,
}) => {
  const canShowEdit =
    (viaTopic && row?.permission?.canViewEditQuestion) || viaSession;
  const canShowDisassociate = viaSession && hasTopicId;
  const canShowDelete =
    (viaTopic && row?.permission?.canViewDeleteQuestion) ||
    (viaSession && !hasTopicId);

  return (
    <TableCell align="center" className="padding-left-1">
      {canShowEdit && (
        <IconButtons
          classList="margin-right-1"
          width={font_13_icon}
          height={font_13_icon}
          disabledImage={edit_disabled_icon}
          image={edit_active_icon}
          handleClick={() => questionEditModal(row?.id, row?.question)}
          tooltip="Edit Question"
          disabled={getEditButtonDisabled(
            viaTopic,
            viaSession,
            hasTopicId,
            isDisabled,
            row
          )}
          disabledTooltip={getEditButtonTooltip(
            viaTopic,
            viaSession,
            hasTopicId,
            isDisabled,
            row
          )}
        />
      )}
      {canShowDisassociate && (
        <IconButtons
          width={font_13_icon}
          height={font_13_icon}
          image={associate_question_default_icon}
          handleClick={() => handleOpenDisassociate(row?.id)}
          tooltip="Disassociate Question"
          disabled={isDisabled}
          disabledImage={associate_question_disabled_icon}
          disabledTooltip={isDisabled ? "Module is completed" : null}
        />
      )}
      {canShowDelete && (
        <IconButtons
          width={font_13_icon}
          height={font_13_icon}
          disabledImage={delete_disabled_icon}
          image={delete_active_icon}
          handleClick={() => handleDeleteModalOpen(row?.id)}
          tooltip="Delete Question"
          disabled={getDeleteButtonDisabled(
            viaTopic,
            viaSession,
            hasTopicId,
            isDisabled,
            row
          )}
          disabledTooltip={getDeleteButtonTooltip(
            viaTopic,
            viaSession,
            isDisabled,
            row
          )}
        />
      )}
    </TableCell>
  );
};

// Component for Question Table Row
const QuestionTableRow = ({
  row,
  index,
  currentPage,
  viewMore,
  viewMoreId,
  handleViewMoreClick,
  ExpandMore,
  canViewActions,
  viaTopic,
  viaSession,
  isDisabled,
  questionEditModal,
  handleOpenDisassociate,
  handleDeleteModalOpen,
}) => {
  const createdDate = dayjs(row?.dateCreated)?.isValid()
    ? getDate(row?.dateCreated)
    : "-";
  const hasTopicId = row?.type === "TOPIC";

  if (row?.deleted) return null;

  return (
    <TableRow className="table-body-row" hover key={index}>
      <TableCell align="center" className="padding-left-1">
        <Typography variant="font_12">
          {index + 1 + (currentPage - 1) * 20}
        </Typography>
      </TableCell>
      <QuestionCell
        question={row?.question}
        questionId={row?.id}
        viewMore={viewMore}
        viewMoreId={viewMoreId}
        handleViewMoreClick={handleViewMoreClick}
        ExpandMore={ExpandMore}
      />
      <TableCell align="left" className="padding-left-1">
        <UserTableAvatar
          imgAlt={row?.createdBy?.fullName}
          imgSrc={row?.createdBy?.profilePictureUrl}
          href={row?.createdBy?.employeeCode}
          label={row?.createdBy?.fullName}
          subLabel={row?.createdBy?.email}
          deleted={row?.createdBy?.deleted}
        />
      </TableCell>
      <TableCell align="left" className="padding-left-1">
        <Typography variant="font_12">{createdDate}</Typography>
      </TableCell>
      {canViewActions && (
        <ActionButtons
          row={row}
          hasTopicId={hasTopicId}
          viaTopic={viaTopic}
          viaSession={viaSession}
          isDisabled={isDisabled}
          questionEditModal={questionEditModal}
          handleOpenDisassociate={handleOpenDisassociate}
          handleDeleteModalOpen={handleDeleteModalOpen}
        />
      )}
    </TableRow>
  );
};

const QuestionTable = ({
  questionList,
  currentPage,
  handlePageChange,
  handleDeleteModalOpen,
  totalPages,
  handleQuestionModalOpen,
  questionEditModal,
  canViewActions,
  isDisabled,
  exerciseCount,
  handleOpenDisassociate,
  moduleIsConcluded,
  viaSession,
  viaTopic,
}) => {
  //////////// handle view more ////////////////////////////////
  const ExpandMore = styled((props) => {
    const { expand, ...other } = props;
    return <IconButton {...other} />;
  })(({ theme, expand }) => ({
    transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
    marginLeft: "auto",
    transition: theme.transitions.create("transform", {
      duration: theme.transitions.duration.shortest,
    }),
  }));

  const [viewMore, setViewMore] = useState(false);
  const [viewMoreId, setViewMoreId] = useState(null);

  const handleViewMoreClick = (value) => {
    if (viewMoreId === value) {
      setViewMoreId(-1);
      setViewMore(false);
    } else {
      setViewMoreId(value);
      setViewMore(true);
    }
  };
  /////////////////////////////////////////////////////////////
  return (
    <>
      <TableContainer className="table-container">
        <Table stickyHeader aria-label="sticky table">
          <TableHead className="table-head-container">
            <TableRow className="table-head-row">
              <TableCell
                align="center"
                sx={{ width: "4rem", maxWidth: "4rem", minWidth: "4rem" }}
                className="padding-left-1"
              >
                <Typography variant="tableHead">S.No.</Typography>
              </TableCell>
              <TableCell
                align="left"
                sx={{ minWidth: "35rem" }}
                className="padding-left-1"
              >
                <Typography variant="tableHead">Questions</Typography>
              </TableCell>
              <TableCell
                align="left"
                sx={{ width: "13rem", maxWidth: "13rem", minWidth: "13rem" }}
                className="padding-left-1"
              >
                <Typography variant="tableHead">Added By</Typography>
              </TableCell>
              <TableCell
                align="left"
                sx={{ width: "6rem", maxWidth: "6rem", minWidth: "6rem" }}
                className="padding-left-1"
              >
                <Typography variant="tableHead">Added on</Typography>
              </TableCell>
              {(viaTopic || canViewActions) && (
                <TableCell
                  align="center"
                  sx={{ width: "6rem", maxWidth: "6rem", minWidth: "6rem" }}
                  className="padding-left-1"
                >
                  <Typography variant="tableHead">Actions</Typography>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {questionList?.map((row, index) => (
              <QuestionTableRow
                key={index}
                row={row}
                index={index}
                currentPage={currentPage}
                viewMore={viewMore}
                viewMoreId={viewMoreId}
                handleViewMoreClick={handleViewMoreClick}
                ExpandMore={ExpandMore}
                canViewActions={canViewActions}
                viaTopic={viaTopic}
                viaSession={viaSession}
                isDisabled={isDisabled}
                questionEditModal={questionEditModal}
                handleOpenDisassociate={handleOpenDisassociate}
                handleDeleteModalOpen={handleDeleteModalOpen}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Grid container className="table-pagination-container">
        <Typography variant="font_16">
          Total {exerciseCount} {exerciseCount > 1 ? "Records" : "Record"}
        </Typography>
        <TablePagination
          totalPages={totalPages}
          handlePageChange={handlePageChange}
          currentPage={currentPage}
        />
      </Grid>
    </>
  );
};

export default QuestionTable;
